import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Chip, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { faArrowDownWideShort, faArrowUpShortWide, faArrowUpWideShort, faCheck, faList, faSortDown, faSortUp, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader } from "../../components/loader/Loader";
import { IntegrationFileStats, IntegrationJobstats } from "../../types/types";
import { fetchIntegrationjobsV2 } from "../../services/integrationService";
import { Download } from "tabler-icons-react";
import { Anchor } from "@mantine/core";
import { isUndefinedorEmpty } from "../transformer/UnregisteredFunctions";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from "@mui/x-date-pickers";
import { GenericInput } from "../../components/inputs/GenericInput";
import { fetchAndDownloadReport } from "../../services/reportService";

const JobStatus = [
  "SUCCESS" , "PENDING" , "RUNNING" , "FAILURE"
]
enum SortType {
  ASC = "Ascending",
  DESC = "Descending",
}

export default function DateFilter(){
  const [startDate, setStartDate] = React.useState<Dayjs | null>(dayjs(new Date().toDateString()));
  const [endDate, setEndDate] = React.useState<Dayjs | null>(dayjs(new Date().toDateString()).set('hour',23).set('minute',59));
  const [statusType, setStatusType] = React.useState<string>("All");
  const [sortType, setSortType] = React.useState<SortType>(SortType.ASC);

  const handleEndDate = (newValue:Dayjs | null) => {
    setEndDate(newValue);
  }
  const curDate:Dayjs = dayjs(new Date());

  const handleStartDate = (newValue: Dayjs | null) => {
    setStartDate(newValue);
  }

  const JobStatusKeys = ["All",...JobStatus]
  
  const sortAsc = sortType === SortType.ASC;

  return (
    <Box >
        <Grid container padding={4} spacing={2}>
          <Grid item xs={12} sm={6} md>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["day"]}
                minDate={curDate.subtract(45, "day")}
                orientation="portrait"
                disableFuture
                label="Start Date"
                inputFormat="DD/MM/YYYY"
                value={startDate}
                onChange={handleStartDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    helperText={
                      curDate.diff(startDate, "day") <= 45
                        ? ""
                        : "Date cannot be more than 45 days in the past"
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["day"]}
                minDate={startDate ? startDate : curDate.subtract(45, "day")}
                orientation="portrait"
                disableFuture
                label="End Date"
                inputFormat="DD/MM/YYYY"
                value={endDate}
                onChange={handleEndDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    helperText={
                      curDate.diff(endDate, "day") <= 45 && (startDate ? startDate.diff(endDate, "day") : 0) <= 0 
                        ? ""
                        : "Date cannot be more than 45 days in the past"
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md>
            <GenericInput
              textFieldInputProps={{
                fullWidth: true,
                select: true,
                label: "Job Status",
                value: statusType,
                children: JobStatusKeys.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                )),
                onChange: (e) => setStatusType(e.target.value),
              }}
            />
          </Grid>
          <Grid item width={'100px'} >
            <Button
              sx={{height:"100%"}}
              title="Sort"
              className="navigation-btn"
              variant="contained"
              onClick={() => setSortType(() => sortAsc ? SortType.DESC : SortType.ASC)}
            >
              <Box paddingRight={'5px'}>SORT</Box>{sortAsc ? <><FontAwesomeIcon icon={faArrowUpShortWide} size="xl"/></> : <><FontAwesomeIcon icon={faArrowDownWideShort} size="xl"/></>}
            </Button>
          </Grid>
        </Grid>
        {startDate && curDate.diff(startDate, "day") <= 45 && curDate.diff(endDate, "day") <= 45 && startDate.diff(endDate, "day") <= 0 && (
        <IntegrationJob
          startDate={startDate}
          endDate={endDate}
          status={statusType}
          sort={sortType}
        />
      )}
    </Box>
  );
}

export function IntegrationJob(props:{startDate:Dayjs | null,endDate:Dayjs | null, status?:string, sort?:SortType}) {
  const [records, setRecords] = useState<IntegrationJobstats[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(20);
  
  const auth = JSON.parse(localStorage.authContext);
  const env = localStorage.getItem("env");

  if(props.sort === SortType.DESC){
    records.sort((a,b) => b.startTime - a.startTime)
  } else {
    records.sort((a,b) => a.startTime - b.startTime)
  }

  const fetchData = useCallback(async (lob: string) => {
    let tableName = env && env === 'prod' ? "integration_job_prod" : "integration_job_uat";
    let filter:any = {
      lob: { eq: lob }
    }
    setIsLoading(true);
    if (props.startDate) {
      filter.startTime = { gt: props.startDate.valueOf() }
    }
    if (props.endDate) {
      filter.endTime = { lt: props.endDate.valueOf() }
    }
    const response = await fetchIntegrationjobsV2({tableName,filter});
    if (response.length > 0)
      setRecords(response.sort((a, b) => a.startTime - b.startTime));
    else 
      setRecords([])
    setIsLoading(false);
  }, [env, props.endDate, props.startDate]);

  useEffect(() => {
    fetchData(auth.user.lob);
  }, [auth.user.lob, fetchData]);

  const filteredRecords = records.filter((record)=> record.status === props.status || props.status === "All")
                                 .map((record)=>record.endTime === -1 ? {...record,endTime:record.startTime}:record)
                                 .filter((record)=> record.endTime < (props.endDate?.valueOf() || Number.MAX_VALUE) )
  return (
    <>
      <Grid container marginY={6} display={"flex"} justifyContent={"space-evenly"}>
      {!isLoading && (filteredRecords.length > 0 ? filteredRecords.slice(0,limit).map((record: IntegrationJobstats, idx: number) => { 
        const statObjects: IntegrationFileStats[] = JSON.parse(record.stats);
        const extraParams: any  = JSON.parse(record.extraParams);
        return <Grid item lg={"auto"} key={idx} className="integration-job-wrap">
            <Card
              key={record.fileId}
              elevation={3}
              sx={{ overflowWrap: "break-word", padding: "2rem", margin: "1rem",}}
            >
              <Box display={"flex"} justifyContent={"space-between"}>
                  <Typography variant="subtitle1" letterSpacing={'-0.6px'} fontWeight={"bold"}>{`${dayjs(new Date(+record.startTime)).format('ddd DD MMM YYYY, HH:mm')} `}
                  { !isUndefinedorEmpty(extraParams.fileKey) && 
                      <Anchor sx={{position:'relative', top:'4px'}} href={`#`}>
                      <Download size={21} style={{lineHeight:0}} onClick={() => { fetchAndDownloadReport(extraParams.fileKey ?? "")}}>
                      </Download>
                      </Anchor>
                  }
                  </Typography>
                <Chip className={"integration-job-status integration-job-status-"+record.status} label={record.status}/>
              </Box>
              <br />
              <Box className="integration-job-table-wrap">
                <FileInfo record={record} extraParams={extraParams}/>
                { statObjects.map((stats,idx) => 
                  <StatsTable key={idx} stats={stats}/>
                )}
              </Box>
            </Card>
          </Grid>
          })
        :<Typography variant="subtitle1" fontWeight={"bold"}>No Records Found</Typography>)}
      </Grid>
      {/* { !isLoading && filteredRecords.length === 0 && <Typography variant="subtitle1" fontWeight={"bold"}>No Records Found</Typography>} */}
      {isLoading ? <Loader /> :
        limit < filteredRecords.length  && <Button sx={{width:"30%",display:"block",margin:"auto", marginBottom:"20px"}} variant="outlined" color="primary" onClick={() => setLimit((limit) => Math.min(limit + 100, records.length))}>Load More</Button>
      }
    </>
  );
}

const FileInfo = (props:{record:IntegrationJobstats, extraParams:any}) =>(
  <Box display={"flex"} flexDirection={"column"} justifyContent={"space-between"} minHeight={"210px"}>
    <Box flex={1}>
      <Typography variant="caption" color="text.secondary">Entity Name</Typography>
      <Typography variant="subtitle2" fontWeight={"bold"}>{props.extraParams.entityName}</Typography>
    </Box>
    <Box flex={1}>
      <Typography variant="caption" color="text.secondary">File Name</Typography>
      <Typography variant="subtitle2" fontWeight={"bold"}>{props.record.fileURL}</Typography>
    </Box>
    <Box flex={1}>
      <Typography variant="caption" color="text.secondary">File Id</Typography>
      <Typography variant="subtitle2" fontWeight={"bold"}>{props.record.fileId}</Typography>
    </Box>
    {isUndefinedorEmpty(props.extraParams.description) ? null : (props.extraParams.description.length > 0 &&
      <Box flex={1}>
        <Typography variant="caption" color="text.secondary">Description</Typography>
        <Typography variant="subtitle2" fontWeight={"bold"}>{props.extraParams.description}</Typography>
      </Box>)
    }
  </Box>
)

const StatsTable = (props:{stats:IntegrationFileStats}) => (
    <Table className="integration-job-table">
      <TableBody>
        <TableRow>
          <TableCell className={"integration-job-status-icon"} style={{ backgroundImage: "linear-gradient(310deg, #17ad37 0%, #98ec2d 100%)", }}>
            <FontAwesomeIcon icon={faCheck}/>
          </TableCell>
          <TableCell align="center">
            <Typography variant="h6" component={"span"} color="text.secondary">{props.stats.success}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="caption" color="text.secondary">Success</Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={"integration-job-status-icon"} style={{ backgroundImage: "linear-gradient(310deg, #ea0606 0%, #ff667c 100%)", }}>
            <FontAwesomeIcon icon={faXmark}/>
          </TableCell>
          <TableCell align="center">
            <Typography variant="h6" component={"span"} color="text.secondary">{props.stats.failure}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="caption" color="text.secondary">Failure</Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={"integration-job-status-icon"} style={{ backgroundImage: "linear-gradient(310deg, #2152ff 0%, #21d4fd 100%)", }}>
            <FontAwesomeIcon icon={faList}/>
          </TableCell>
          <TableCell align="center">
            <Typography variant="h6" component={"span"} color="text.secondary">{props.stats.total}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="caption" color="text.secondary">Total</Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
)